import { Radio, SegmentedControl, Select, TextInput } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import styled from 'styled-components';

export const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 0;
	height: 100%;
	background-color: #fff;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	border-radius: 12px;
	overflow: hidden;
`;

/**
 * `dark[0]`, not `gray[6]`.
 *
 * v7's grey ramp put `gray[6]` at #BDC4C8 — 1.9:1 on white, which at 11px reads as a smudge
 * rather than a heading. #4F616C clears AA for small text and is still plainly secondary.
 */
export const EyebrowLabel = styled.span`
	display: block;
	font-size: 0.6875rem;
	font-weight: 600;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: ${({ theme }) => theme.v3.colors.dark[0]};
`;

export const Header = styled.div`
	padding: 0.875rem 0.875rem 0.625rem;
	display: flex;
	flex-direction: column;
	gap: 0.375rem;
	flex-shrink: 0;
`;

export const HeaderRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
`;

export const HeaderActions = styled.div`
	display: flex;
	align-items: center;
	gap: 0.25rem;
	flex-shrink: 0;

	// Small red-tinted trash button (removes the field entirely), distinct
	// from the plain close (x) button beside it.
	.trash-action-icon {
		background-color: ${({ theme }) => theme.v3.colors.red[0]};

		&:hover {
			background-color: ${({ theme }) => theme.v3.colors.red[1]};
		}
	}
`;

// Borderless-until-focused so it reads as a bold title at rest but is
// clearly editable once the user interacts with it.
export const LabelInput = styled(TextInput)`
	flex: 1;
	min-width: 0;

	input {
		font-size: 1.125rem;
		font-weight: 700;
		border-color: transparent;
		background-color: transparent;
		padding-left: 0;

		&:hover {
			border-color: ${({ theme }) => theme.v3.colors.gray[3]};
		}

		&:focus {
			border-color: ${({ theme }) => theme.v3.colors.blue[5]};
			background-color: #fff;
			padding-left: 0.5rem;
		}
	}
`;

// Scrolling region: everything between the header and the footer, so the
// stepper, tabs, and "resolve to" card scroll away together with the
// data-point list rather than each fighting for space.
export const ScrollBody = styled.div`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	padding: 0 0.875rem 0.875rem;
`;

export const ResolveToCardBox = styled.div<{ $variant: 'empty' | 'selected' }>`
	border-radius: 8px;
	padding: 0.75rem;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	flex-shrink: 0;
	background-color: ${({ $variant, theme }) =>
		$variant === 'selected' ? theme.v3.colors.blue[0] : theme.v3.colors.gray[0]};
	border: 1px solid
		${({ $variant, theme }) =>
			$variant === 'selected' ? theme.v3.colors.blue[2] : 'transparent'};
`;

export const ChipRow = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.25rem;
`;

export const ChipGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 0.25rem;
`;

export const ChipPlus = styled(IconPlus)`
	color: ${({ theme }) => theme.v3.colors.gray[5]};
	flex-shrink: 0;
`;

export const Chip = styled.div`
	display: flex;
	align-items: center;
	gap: 0.3rem;
	background-color: #fff;
	border: 1px solid ${({ theme }) => theme.v3.colors.blue[2]};
	border-radius: 999px;
	padding: 0.2rem 0.4rem 0.2rem 0.5rem;
	color: ${({ theme }) => theme.v3.colors.blue[7]};
`;

export const ChipRemove = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	padding: 0.1rem;
	cursor: pointer;
	color: ${({ theme }) => theme.v3.colors.gray[6]};

	&:hover {
		color: ${({ theme }) => theme.v3.colors.gray[8]};
	}
`;

export const JoinWithRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;

	> p {
		flex-shrink: 0;
		white-space: nowrap;
	}
`;

export const JoinWithControl = styled(SegmentedControl)`
	flex: 1;
	min-width: 0;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[3]};
	border-radius: 6px;

	label,
	.mantine-SegmentedControl-label {
		white-space: nowrap;
	}

	/* Color Mantine's rounded floating indicator (the pill) rather than the
	   square control wrapper — that keeps the selection's corners rounded. */
	.mantine-SegmentedControl-indicator {
		background-color: ${({ theme }) => theme.v3.colors.blue[6]};
		border-radius: 5px;
	}

	[data-active] {
		color: #fff;
	}
`;

export const DashedDivider = styled.div`
	border-top: 1px dashed ${({ theme }) => theme.v3.colors.blue[2]};
`;

export const PreviewRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.15rem;
	word-break: break-word;
`;

export const SelectDataPointHeading = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	flex-shrink: 0;
`;

export const SearchInput = styled(TextInput)`
	flex-shrink: 0;
`;

// Bounded scroll region for the grouped data-point list. `min-height: 0` is
// load-bearing here: without it this element sizes to its content instead of
// the space left over in `ScrollBody`, and the list never scrolls.
export const CatalogueScroller = styled.div`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

export const GroupSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.4rem;
`;

/** Colours the group's ICON; the label beside it carries its own. Darkened with the eyebrow. */
export const GroupLabel = styled.div`
	display: flex;
	align-items: center;
	gap: 0.35rem;
	padding: 0.25rem 0;
	color: ${({ theme }) => theme.v3.colors.dark[0]};
`;

export const DataPointRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	border-radius: 8px;
	background-color: #fff;
`;

export const DataPointIconTile = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 2.25rem;
	height: 2.25rem;
	border-radius: 8px;
	flex-shrink: 0;
	background-color: ${({ theme }) => theme.v3.colors.blue[0]};
	color: ${({ theme }) => theme.v3.colors.blue[6]};
`;

export const RowText = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
	flex: 1;
`;

export const SampleValuePill = styled.div`
	background-color: ${({ theme }) => theme.v3.colors.gray[2]};
	border-radius: 999px;
	padding: 0.15rem 0.55rem;
	flex-shrink: 0;
	max-width: 40%;
	overflow: hidden;

	p {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`;

export const ToggleButton = styled.button<{ $selected: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 1.75rem;
	height: 1.75rem;
	border-radius: 6px;
	flex-shrink: 0;
	cursor: pointer;
	border: 1px solid
		${({ $selected, theme }) => ($selected ? theme.v3.colors.blue[6] : theme.v3.colors.gray[3])};
	background-color: ${({ $selected, theme }) => ($selected ? theme.v3.colors.blue[6] : '#fff')};
	color: ${({ $selected, theme }) => ($selected ? '#fff' : theme.v3.colors.gray[6])};

	&:hover {
		border-color: ${({ theme }) => theme.v3.colors.blue[6]};
	}
`;

// Compact single/list mode toggle at the top of the scroll body. Mirrors the
// SegmentedTabs look but is interactive (not read-only) and full width.
export const ModeToggle = styled(SegmentedControl)`
	flex-shrink: 0;
	background-color: ${({ theme }) => theme.v3.colors.gray[0]};

	label {
		white-space: nowrap;
	}

	.mantine-SegmentedControl-indicator {
		background-color: #fff;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
	}
`;

// "FOR EACH …" collection picker at the top of the list card.
export const CollectionSelect = styled(Select)`
	flex: 1;
	min-width: 0;
`;

export const ForEachRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;

	> span {
		flex-shrink: 0;
		white-space: nowrap;
	}
`;

// Empty-state shell: same white card as the panel it stands in for, so the
// side column doesn't visibly change shape when a field is selected.
export const EmptyStateWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	height: 100%;
	padding: 2rem 2.25rem;
	text-align: center;
	background-color: #fff;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	border-radius: 12px;
`;

// Dashed for the "draw one yourself" prompt, solid for "pick one that exists" —
// the same visual language the canvas legend uses for unbound vs bound boxes.
export const EmptyStateIconTile = styled.div<{ $variant: 'dashed' | 'solid' }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 3.5rem;
	height: 3.5rem;
	border-radius: 12px;
	margin-bottom: 0.25rem;
	color: ${({ theme }) => theme.v3.colors.blue[6]};
	background-color: ${({ theme }) => theme.v3.colors.blue[0]};
	border: 2px ${({ $variant }) => ($variant === 'dashed' ? 'dashed' : 'solid')}
		${({ theme }) => theme.v3.colors.blue[2]};
`;

// NB: gray[1] is NOT a light tint in this theme — it is #BDC4C8, the same value
// as gray[6], which is what Mantine's `dimmed` resolves to. Dimmed text on a
// gray[1] pill is literally invisible. Blue tint + blue[7] text is 6.4:1.
export const EmptyStateRemainingPill = styled.div`
	margin-top: 0.25rem;
	padding: 0.2rem 0.6rem;
	border-radius: 999px;
	background-color: ${({ theme }) => theme.v3.colors.blue[0]};
`;

// Save bar pinned under the side column. It is a sibling of the panel rather
// than a child of it, so it survives deselecting and deleting the last field.
export const PersistentFooter = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-shrink: 0;
	padding: 0.75rem 0.875rem;
	background-color: #fff;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	border-radius: 12px;
`;

export const DirtyHint = styled.div<{ $isDirty: boolean }>`
	display: flex;
	align-items: center;
	gap: 0.25rem;
	margin-right: auto;
	min-width: 0;
	color: ${({ $isDirty, theme }) =>
		$isDirty ? theme.v3.colors.orange[7] : theme.v3.colors.dark[0]};
`;

// "WHICH SECURITY?" card: the security picker + transaction-scope radio.
export const SecuritySelect = styled(Select)`
	flex: 1;
	min-width: 0;
`;

export const SecurityScopeRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 6px;
`;

export const TransactionScopeRadio = styled(Radio.Group)`
	margin-top: 10px;
`;

export const ScopeWarningRow = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 6px;
	margin-top: 10px;
	padding: 8px 10px;
	border-radius: 6px;
	background-color: ${({ theme }) => theme.v3.colors.yellow[0]};
	color: ${({ theme }) => theme.v3.colors.yellow[9]};
`;

/** The same row, in neutral tones: "this has not been checked" is not a warning. */
export const ScopeNoticeRow = styled(ScopeWarningRow)`
	background-color: ${({ theme }) => theme.v3.colors.gray[0]};
	color: ${({ theme }) => theme.v3.colors.gray[7]};
`;

export const ApplyToAllLink = styled.button`
	align-self: flex-start;
	margin-top: 8px;
	padding: 0;
	border: none;
	background: none;
	font-size: 12px;
	text-decoration: underline;
	cursor: pointer;
	color: ${({ theme }) => theme.v3.colors.blue[6]};
`;
